import React, { forwardRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isWithinInterval, format, parseISO, differenceInDays } from 'date-fns';
import PropTypes from 'prop-types';

import { setDateTimeFromTimeString } from '@/components/SchedulePage/helpers/setDateTimeFromTimeString';
import OrderCardComponent from '@/components/SchedulePage/OrderCard/OrderCardComponent';
import validateAllowedAtDateFormat from '@/components/SchedulePage/OrderCard/validateAllowedAtDateFormat';
import { useUser } from '@/contexts/UserContext';
import {
  setSelectedOrder,
  notifyFilteredInspectors,
  clearFilteredInspectorsNotifications,
} from '@/store/ducks/schedule';
import { getScheduleDate, getTodayUnavailabilitiesGroupedByInspector } from '@/store/selectors/schedule';

const localKeysTypes = ['No local', 'Local'];
const realEstateKeysTypes = ['Na imobiliária', 'Imobiliária'];

const OrderCardContainer = (
  { startOffset = null, endOffset = null, inspector = null, inTimeline = false, order: { order, schedule = null }, ...props },
  ref
) => {
  const dispatch = useDispatch();
  const scheduleDateAsDateObject = useSelector(getScheduleDate);
  const ordersThatAreBeingEdited = useSelector(state => state.schedule?.ordersThatAreBeingEdited);
  const todayUnavailabilitiesGrouped = useSelector(getTodayUnavailabilitiesGroupedByInspector);
  const inspectorTodayUnavailabilities = useMemo(() => {
    return (inspector?.public_id && todayUnavailabilitiesGrouped[inspector?.public_id]) || [];
  }, [todayUnavailabilitiesGrouped, inspector?.public_id]);

  const me = useUser();

  const isAlreadyBeingEditedByAnotherUser = useMemo(() => {
    const orderPublicId = order?.public_id || order?.id;
    return ordersThatAreBeingEdited.some(orderThatAreBeingEdited => {
      const publicIdIsEqual = orderThatAreBeingEdited?.order?.order?.public_id === orderPublicId;
      const isAnotherUserEditing = orderThatAreBeingEdited?.sender?.id !== me?.uniqueId;

      return publicIdIsEqual && isAnotherUserEditing;
    });
  }, [me, order, ordersThatAreBeingEdited]);

  const insideTimelineStyle = useMemo(() => {
    return (
      startOffset &&
      endOffset && {
        position: 'absolute',
        top: '50%',
        left: startOffset || 0,
        width: endOffset - startOffset + 1,
        transform: 'translate(-15px, -50%)',
        opacity: inspector?.wasNotFiltered ? 0.5 : 1,
      }
    );
  }, [endOffset, inspector, startOffset]);

  const boundSetSelectedOrder = orderId => dispatch(setSelectedOrder(orderId));

  const handleDragStart = (data, evt) => {
    const { currentTarget: target } = evt;
    const rect = target.getBoundingClientRect();
    const offsetX = evt.clientX - rect.x;

    const { order: draggedOrder, schedule: draggedSchedule } = data;

    dispatch(
      notifyFilteredInspectors({
        address_id: draggedOrder?.address_id,
        order_type_id: draggedOrder?.order_type_id,
      })
    );

    target.style.opacity = 0.4;
    evt.dataTransfer.dropEffect = 'move';
    evt.dataTransfer.effectAllowed = 'move';
    evt.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        order: {
          public_id: draggedOrder?.public_id || draggedOrder?.id,
          id: draggedOrder?.public_id || draggedOrder?.id,
          client: draggedOrder?.client,
          code: draggedOrder?.code,
          identifier: draggedOrder?.identifier,
          type: draggedOrder?.type,
          credits: draggedOrder?.credits,
          address_id: draggedOrder?.address_id,
          order_type_id: draggedOrder?.order_type_id,
          building_type: draggedOrder?.building_type,
          allowed_at_date: draggedOrder?.allowed_at_date || null,
          accompanied_inspection: draggedOrder?.accompanied_inspection,
          furnished: draggedOrder?.furnished,
          modality: draggedOrder?.modality,
          urgency: draggedOrder?.urgency,
          created_at: draggedOrder?.created_at,
          access_information: draggedOrder?.access_information,
          details: draggedOrder?.details,
          search_tokens: draggedOrder?.search_tokens,
          status: draggedOrder?.status,
          city: draggedOrder?.city,
          neighborhood: draggedOrder?.neighborhood,
          street: draggedOrder?.street,
          number: draggedOrder?.number,
          complement: draggedOrder?.complement,
          state: draggedOrder?.state,
          zipcode: draggedOrder?.zipcode,
        },
        schedule: draggedSchedule,
        offsetX,
      })
    );
  };

  const handleDragEnd = evt => {
    const { currentTarget: target } = evt;
    target.style.opacity = 1;
  };

  const onDragEndCapture = useCallback(() => {
    dispatch(clearFilteredInspectorsNotifications());
  }, [dispatch]);

  const parseDate = value => {
    if (!value) return new Date(NaN);
    if (value instanceof Date) return value;
    if (typeof value === 'object' && value.date) {
      value = value.date;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      let parsed = parseISO(trimmed);
      if (parsed instanceof Date && !isNaN(parsed.getTime())) {
        return parsed;
      }
      parsed = new Date(trimmed);
      if (parsed instanceof Date && !isNaN(parsed.getTime())) {
        return parsed;
      }
      const dmyRegex = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;
      const match = trimmed.match(dmyRegex);
      if (match) {
        const [_, day, month, year, hour = 0, minute = 0, second = 0] = match;
        parsed = new Date(year, month - 1, day, hour, minute, second);
        if (parsed instanceof Date && !isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    }
    if (typeof value === 'number') {
      return new Date(value);
    }
    return new Date(NaN);
  };

  const handleGetLockIconState = useCallback(() => {
    let lockIconState = false;
    let formattedAllowedAtDate = '';

    if (order?.allowed_at_date === null) {
      lockIconState = null;
    } else {
      const validatedDate = validateAllowedAtDateFormat(String(order?.allowed_at_date).trim());

      if (validatedDate) {
        const scheduleDate = parseDate(scheduleDateAsDateObject);
        const parsedValidatedDate = parseDate(validatedDate);

        if (
          scheduleDate instanceof Date && !isNaN(scheduleDate.getTime()) &&
          parsedValidatedDate instanceof Date && !isNaN(parsedValidatedDate.getTime())
        ) {
          lockIconState = differenceInDays(scheduleDate, parsedValidatedDate) >= 1;
        } else {
          lockIconState = false;
        }

        formattedAllowedAtDate = (parsedValidatedDate instanceof Date && !isNaN(parsedValidatedDate.getTime()))
          ? format(parsedValidatedDate, 'dd/MM/yyyy')
          : '';
      }
    }

    return {
      lockIconState,
      formattedAllowedAtDate,
    };
  }, [order?.allowed_at_date, scheduleDateAsDateObject]);

  const handleGetKeyIconState = useCallback(() => {
    if (localKeysTypes.includes(order?.access_information?.type)) return 'ACCESS_INFORMATION_LOCAL_KEYS_TYPE';

    if (localKeysTypes.includes(order?.property_access?.type)) return 'PROPERTY_ACCESS_LOCAL_KEYS_TYPE';

    if (realEstateKeysTypes.includes(order?.access_information?.type))
      return 'ACCESS_INFORMATION_REAL_ESTATE_KEYS_TYPE';

    if (realEstateKeysTypes.includes(order?.property_access?.type)) return 'PROPERTY_ACCESS_REAL_ESTATE_KEYS_TYPE';

    if (order?.access_information?.type === 'Outro') return 'ACCESS_INFORMATION_OTHER_KEYS_TYPE';

    if (order?.property_access?.type === 'Outro') return 'PROPERTY_ACCESS_OTHER_KEYS_TYPE';

    if (order?.access_information?.keysLocation) return 'ACCESS_INFORMATION_KEYS_LOCATION_KEYS_TYPE';

    if (order?.property_access?.keysLocation) return 'PROPERTY_ACCESS_DETAILS_KEYS_TYPE';

    return false;
  }, [
    order?.access_information?.keysLocation,
    order?.access_information?.type,
    order?.property_access?.keysLocation,
    order?.property_access?.type,
  ]);

  const { formattedAllowedAtDate, lockIconState } = handleGetLockIconState();
  const keyIconState = handleGetKeyIconState();

  const formattedCreatedAtDate = (() => {
    if (!order?.created_at) return '';
    const date = parseDate(order.created_at);
    return date instanceof Date && !isNaN(date.getTime()) ? format(date, 'dd/MM/yyyy') : '';
  })();

  const orderAddressParts = [
    order?.zipcode && `${order.zipcode}, `,
    order?.street && `${order.street}, `,
    order?.number && `${order.number}, `,
    order?.complement && `${order.complement}, `,
    order?.neighborhood && `${order.neighborhood}, `,
    order?.city && `${order.city} - `,
    order?.state,
  ];

  const formattedOrderAddress = orderAddressParts.filter(Boolean).join('');

  const isUnavailable = useMemo(() => {
    const inspectorTodayUnavailabilitiesList = inspectorTodayUnavailabilities || [];

    if (inspectorTodayUnavailabilitiesList.length) {
      const scheduleStartDate = setDateTimeFromTimeString(scheduleDateAsDateObject, schedule?.when?.start_time);
      const scheduleEndDate = setDateTimeFromTimeString(scheduleDateAsDateObject, schedule?.when?.end_time);

      const isScheduleStartValid = scheduleStartDate instanceof Date && !isNaN(scheduleStartDate.getTime());
      const isScheduleEndValid = scheduleEndDate instanceof Date && !isNaN(scheduleEndDate.getTime());

      if (!isScheduleStartValid || !isScheduleEndValid) {
        return false;
      }

      return inspectorTodayUnavailabilitiesList.some(unavailability => {
        const start = parseDate(unavailability.start);
        const end = parseDate(unavailability.end);

        const isStartValid = start instanceof Date && !isNaN(start.getTime());
        const isEndValid = end instanceof Date && !isNaN(end.getTime());

        if (!isStartValid || !isEndValid) {
          return false;
        }

        const interval = { start, end };

        return isWithinInterval(scheduleStartDate, interval) || isWithinInterval(scheduleEndDate, interval);
      });
    }

    return false;
  }, [inspector, scheduleDateAsDateObject, schedule, inspectorTodayUnavailabilities]);

  return (
    <OrderCardComponent
      order={order}
      schedule={schedule}
      currentScheduleUser={me?.uniqueId}
      setSelectedOrder={boundSetSelectedOrder}
      insideTimelineStyle={insideTimelineStyle}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragEndCapture={onDragEndCapture}
      isAlreadyBeingEditedByAnotherUser={isAlreadyBeingEditedByAnotherUser}
      isUnavailable={isUnavailable}
      inTimeline={inTimeline}
      lockIconState={lockIconState}
      keyIconState={keyIconState}
      formattedAllowedAtDate={formattedAllowedAtDate}
      formattedCreatedAtDate={formattedCreatedAtDate}
      formattedOrderAddress={formattedOrderAddress}
      forwardedRef={ref}
      {...props}
    />
  );
};

OrderCardContainer.propTypes = {
  startOffset: PropTypes.number,
  endOffset: PropTypes.number,
  inspector: PropTypes.object,
  order: PropTypes.object.isRequired,
  schedule: PropTypes.object,
  inTimeline: PropTypes.bool,
};

export default forwardRef(OrderCardContainer);
